'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { Link } from '@/shared/i18n'
import { cn, formatDate } from '@/shared/lib'
import { Icon, Spinner } from '@/shared/ui'

import { PlaceholderThumbnail } from './placeholder-thumbnail'
import { DashboardThumbnail, ThumbnailGroup } from './thumbnail-group'

type DashboardState = {
  isLoading: boolean
}

const DashboardContext = createContext<DashboardState | null>(null)

const useDashboardContext = () => {
  const state = useContext(DashboardContext)

  return state ?? { isLoading: false }
}

type CardProps = {
  className?: string
  children: React.ReactNode
  isLoading?: boolean
}

export const Card = ({ className, children, isLoading = false }: CardProps) => {
  return (
    <DashboardContext.Provider value={{ isLoading }}>
      <div
        className={cn(
          'h-[224px] rounded-lg bg-white/50 p-6 shadow-[0px_1px_7px_0px_rgba(0,0,0,0.05)]',
          className,
        )}
      >
        {children}
      </div>
    </DashboardContext.Provider>
  )
}

export const Header = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="mb-6 flex items-center justify-between">{children}</div>
  )
}

export const Title = ({ children }: { children: React.ReactNode }) => {
  return <h3 className="typo-title3 font-semibold text-black">{children}</h3>
}

type RefreshProps = {
  time: string
  onRefresh: () => void
  isRefreshing: boolean
}

export const Refresh = ({ time, onRefresh, isRefreshing }: RefreshProps) => {
  const [isMounted, setIsMounted] = useState(false)

  const formattedTime = formatDate({ date: time, format: 'HH:mm' })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <div className="flex cursor-pointer items-center gap-1" onClick={onRefresh}>
      <span className="typo-label1-reading font-light text-gray-500">
        {formattedTime} 기준
      </span>
      <Icon
        name="refresh"
        className={cn(
          'text-gray-500',
          isMounted && isRefreshing && 'animate-spin',
        )}
        size={16}
      />
    </div>
  )
}

type ContentProps = {
  children: React.ReactNode
  className?: string
  columns?: 1 | 2 | 4
}

export const Content = ({ children, className, columns = 4 }: ContentProps) => {
  return (
    <div
      className={cn(
        `grid gap-6`,
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-2',
        columns === 4 && 'grid-cols-4',
        className,
      )}
    >
      {children}
    </div>
  )
}

type ItemRootProps = {
  className?: string
  children: React.ReactNode
  href: string
}

export const Item = ({ className, children, href }: ItemRootProps) => {
  const { isLoading } = useDashboardContext()

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isLoading) {
        e.preventDefault()
      }
    },
    [isLoading],
  )

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={cn(
        'group relative block h-[116px] rounded-lg bg-white p-4 shadow-[0px_1px_7px_0px_rgba(0,0,0,0.15)] transition-colors duration-300',
        !isLoading && 'cursor-pointer hover:bg-gray-50',
        className,
      )}
    >
      {isLoading ? <Spinner /> : children}
    </Link>
  )
}

type ItemProps = {
  title: string
  count: number
  href: string
  thumbnails: DashboardThumbnail[]
}

export const ContractItem = ({ title, count, href, thumbnails }: ItemProps) => {
  return (
    <Item href={href}>
      <h3 className="typo-body1 font-medium">{title}</h3>
      <div className="mt-3 flex items-center justify-between">
        <span className="typo-title1 font-bold">{count}</span>
        {(() => {
          if (thumbnails.length === 0) {
            return <PlaceholderThumbnail />
          }

          return <ThumbnailGroup title={title} thumbnails={thumbnails} />
        })()}
      </div>
    </Item>
  )
}
